import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dag46nhtto0ceqqnhllg";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [nickname, setNickname] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：命中蜜罐字段则不提交，仅给出与正常提交一致的提示
    const honey = (fd.get("company_alt") as string)?.trim() ?? "";
    if (honey) {
      setStatus("success");
      form.reset();
      return;
    }

    if (!nickname.trim()) {
      setStatus("error");
      setFormError("请先填写你的昵称或称呼。");
      return;
    }

    setStatus("sending");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) ?? {};
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        "";
      const code = parsed?.code as string;

      const spamHit = !res.ok || code !== "OK" || /spam/i.test(responseText) || /spam/i.test(serverMsg);
      if (spamHit) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交，请稍后再试。");
    }
  };

  return (
    <section className="bg-background-100/60 px-4 py-14 md:px-6 md:py-16" aria-label="提交话题或反馈建议">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-500">联系我们</span>
            <h2 className="mt-2 font-[ZCOOL_KuaiLe] text-3xl leading-snug text-foreground-950">
              有料的话题，
              <span className="text-primary-500">告诉我们</span>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600">
              发现有趣的热点、想分享的观点，或对我们有任何建议，都可以通过表单告诉我们。我们会认真阅读每一份反馈，择优收录进话题区，一起把这里建设得更好。
            </p>
            <div className="mt-6 space-y-3 text-sm text-foreground-600">
              <p className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                商务与媒体合作：hello@nana51-reyi.example
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                内容更新：每天 08:00 - 23:00（人工与工具协同）
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-shield-check-line text-primary-500"></i>
                我们尊重隐私，绝不泄露你的联系方式
              </p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-readdy-form="娜娜51话题反馈表单"
            className="rounded-2xl border border-background-200 bg-background-50 p-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col">
                <label htmlFor="nickname" className="mb-1.5 text-sm text-foreground-700">
                  你的昵称 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="怎么称呼你"
                  className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="email" className="mb-1.5 text-sm text-foreground-700">
                  联系邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col">
              <label htmlFor="topic_type" className="mb-1.5 text-sm text-foreground-700">
                反馈类型
              </label>
              <select
                id="topic_type"
                name="topic_type"
                className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                <option value="推荐话题">推荐一个热议话题</option>
                <option value="观点分享">分享我的观点</option>
                <option value="内容建议">内容 / 栏目建议</option>
                <option value="问题反馈">遇到问题反馈</option>
              </select>
            </div>

            <div className="mt-4 flex flex-col">
              <label htmlFor="content" className="mb-1.5 text-sm text-foreground-700">
                具体内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="content"
                name="content"
                required
                rows={4}
                maxLength={500}
                placeholder="请用简洁的语言描述你想推荐的话题或建议（最多 500 字）"
                className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              ></textarea>
            </div>

            {/* 反垃圾蜜罐字段，用户不可见、不可填 */}
            <div className="antispam-honey-field">
              <label htmlFor="company_alt">公司名称</label>
              <input id="company_alt" name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            {status === "error" && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-primary-50 px-3 py-2.5 text-sm text-primary-700" role="alert">
                <i className="ri-error-warning-line mt-0.5"></i>
                {formError || "提交失败，请稍后再试。"}
              </p>
            )}
            {status === "success" && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-secondary-100 px-3 py-2.5 text-sm text-secondary-800">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                感谢你的反馈！我们已经收到，核实后会尽快处理并择优收录。
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-5 inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "sending" ? (
                <>
                  <i className="ri-loader-4-line mr-1 animate-spin"></i>
                  提交中…
                </>
              ) : (
                <>
                  提交反馈 <i className="ri-send-plane-line ml-1"></i>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}